import React from 'react';
import { Link } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { GraduationCap, Gem, ListChecks, Sparkles } from 'lucide-react';
import { AcademyTab, SpecialistsTab } from './ReaperCast';
import { ACTION_CENTER_TRACKS } from '../data/actionCenterIndex';

/** /academy — top-level Music Biz Academy hub.
 * Three tabs:
 *  · Curriculum   — the existing AcademyTab (tracks · lessons · quizzes · certs)
 *  · Specialists  — unlocked AI specialist coaches (lives inside Academy now)
 *  · Action Centers — per-track artist toolkits (checklists + tools + reads)
 */
export default function Academy() {
  return (
    <div className="max-w-[1240px] mx-auto" data-testid="academy-page">
      <header className="mb-7">
        <div className="flex items-center gap-2 text-[#ffcf52]"><GraduationCap className="w-5 h-5" /><span className="uppercase text-[11px] tracking-widest font-bold">Music Biz Academy</span></div>
        <h1 className="font-display text-[36px] md:text-[54px] mt-2 leading-[1.02]">Learn the <span className="brand-gradient">business of music</span>.</h1>
        <p className="text-white/65 text-[15px] mt-3 max-w-[720px]">7 tracks · 38 lessons · 7 unlockable AI specialists. Finish a track to earn a shareable certificate AND deepen each topic with the per-track Action Center.</p>
      </header>

      <Tabs defaultValue="curriculum">
        <TabsList className="bg-white/[0.04] border border-white/10 flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="curriculum" data-testid="academy-tab-curriculum"><GraduationCap className="w-4 h-4 mr-1.5" /> Curriculum</TabsTrigger>
          <TabsTrigger value="action-centers" data-testid="academy-tab-action-centers"><ListChecks className="w-4 h-4 mr-1.5" /> Action Centers</TabsTrigger>
          <TabsTrigger value="specialists" data-testid="academy-tab-specialists"><Gem className="w-4 h-4 mr-1.5" /> Specialists</TabsTrigger>
        </TabsList>

        <TabsContent value="curriculum" className="mt-6"><AcademyTab /></TabsContent>
        <TabsContent value="action-centers" className="mt-6"><ActionCentersIndex /></TabsContent>
        <TabsContent value="specialists" className="mt-6"><SpecialistsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function ActionCentersIndex() {
  return (
    <div data-testid="academy-action-centers-index">
      <div className="card-shell p-5 mb-5 flex items-start gap-3" style={{ background: 'linear-gradient(135deg, rgba(255,207,82,0.1), rgba(255,46,99,0.06))', borderColor: 'rgba(255,207,82,0.3)' }}>
        <span className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: 'rgba(255,207,82,0.18)', color: '#ffcf52' }}><Sparkles className="w-5 h-5" /></span>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider font-bold text-[#ffcf52]">What's an Action Center?</div>
          <p className="text-[13.5px] text-white/75 mt-1 leading-relaxed">For each Academy track, a dedicated toolkit: a <span className="text-white font-semibold">prioritized checklist</span> of concrete next steps, <span className="text-white font-semibold">tools &amp; templates</span>, and <span className="text-white font-semibold">deeper-reading resources</span>. Reading the lessons builds knowledge — the Action Center turns it into income.</p>
        </div>
      </div>

      <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="action-center-list">
        {ACTION_CENTER_TRACKS.map(t => (
          <li key={t.id}>
            <Link to={`/academy/action-center/${t.id}`} className="card-shell p-4 hover-lift block h-full" data-testid={`action-center-tile-${t.id}`}>
              <div className="w-10 h-10 rounded-xl grid place-items-center mb-2" style={{ background: t.color + '22', color: t.color }}>
                <ListChecks className="w-5 h-5" />
              </div>
              <h3 className="font-display text-[16px] leading-tight">{t.name}</h3>
              <p className="text-[12.5px] text-white/60 mt-1.5 leading-relaxed">{t.tagline}</p>
              <div className="text-[11px] text-white/45 mt-3 flex items-center gap-2">
                <span>{t.checklist_count} action steps</span>
                <span>·</span>
                <span>{t.tools_count} tools</span>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
